import '../../i18n';
import {useTranslation} from 'react-i18next';
import React from 'react';
import styled, { css } from 'styled-components';
import Markdown from 'markdown-to-jsx';
import { FiCopy, FiEdit2, FiMaximize2 } from 'react-icons/fi';
import NoteEditor from './NoteEditor';
import ExpandablePreviewText from './ExpandablePreviewText';
import CarouselModal from '../Modals/CarouselModal';

import {
    PALE_WHITE_1,
    WHITE
} from '../../appearance/Colors';
import {
    createMarkdownToJsxOptions,
    normalizeMarkdownForStorage,
    shouldRenderMarkdownAsPlainText
} from '../../util/markdownTheme';

function NoteSection({
    isNoteMode,
    messageId,
    inline = false,
    isEditing,
    values,
    setFieldValue,
    onBackToMessage,
    onOpenNote,
    onStartEditing,
    noteEditorMode,
    onNoteEditorModeChange,
    onCopyNote,
    onTaskCheckboxToggle,
    readOnly = false,
    showHoverActions = true
}) {
    const {t: lnT} = useTranslation();

    const noteText = values.body || '';
    const normalizedNoteText = normalizeMarkdownForStorage(noteText);
    const displayText = normalizedNoteText;
    const shouldRenderPlainText = shouldRenderMarkdownAsPlainText(displayText);
    const [previewImage, setPreviewImage] = React.useState(null);
    const [noteExpanded, setNoteExpanded] = React.useState(false);
    React.useEffect(() => setNoteExpanded(false), [messageId]);
    const handleOpenImagePreview = React.useCallback(image => {
        if (!image?.src) {
            return;
        }

        setPreviewImage({
            src: image.src,
            alt: image.alt || lnT('messages:embeddedImage')
        });
    }, [lnT, ]);
    const fullPreviewMarkdownOptions = React.useMemo(
        () =>
            createMarkdownToJsxOptions({
                surface: 'dark',
                bodyFontSizePx: 14,
                onImageClick: handleOpenImagePreview,
                onTaskCheckboxToggle: readOnly ? undefined : onTaskCheckboxToggle
            }),
        [lnT, readOnly, handleOpenImagePreview, onTaskCheckboxToggle]
    );
    const compactCardMarkdownOptions = React.useMemo(
        () =>
            createMarkdownToJsxOptions({
                surface: 'dark',
                bodyFontSizePx: 14,
                onImageClick: handleOpenImagePreview,
                onTaskCheckboxToggle: readOnly ? undefined : onTaskCheckboxToggle
            }),
        [lnT, readOnly, handleOpenImagePreview, onTaskCheckboxToggle]
    );

    if (readOnly && !displayText) return null;

    if (isEditing && !readOnly) {
        return (
            <>
                <NoteEditor
                    inline={inline}
                    values={values}
                    setFieldValue={setFieldValue}
                    onStopEditing={onBackToMessage}
                    mode={noteEditorMode}
                    onModeChange={onNoteEditorModeChange}
                    onOpenImagePreview={handleOpenImagePreview}
                />
                <CarouselModal
                    readOnly={readOnly}
                    allowExtraction={false}
                    isOpen={Boolean(previewImage)}
                    closeModal={event => {
                        if (event?.stopPropagation) {
                            event.stopPropagation();
                        }
                        setPreviewImage(null);
                    }}
                    items={previewImage ? [{ src: previewImage.src }] : []}
                    fileType="Image"
                />
            </>
        );
    }

    if (isNoteMode) {
        return (
            <>
                <FullPreviewContainer
                    data-note-preview
                    onClick={event => {
                        if (event.target instanceof Element && event.target.closest('a, button, input, img, video')) {
                            return;
                        }
                        event.preventDefault();
                        event.stopPropagation();
                        if (!readOnly) onStartEditing();
                    }}
                >
                    <FullPreviewContent>
                        {displayText ? (
                            shouldRenderPlainText ? (
                                <PlainTextPreview>{displayText}</PlainTextPreview>
                            ) : (
                                <Markdown options={fullPreviewMarkdownOptions}>{displayText}</Markdown>
                            )
                        ) : (
                            <NotePreviewPlaceholder>{lnT('messages:writeYourNoteHere')}</NotePreviewPlaceholder>
                        )}
                    </FullPreviewContent>
                </FullPreviewContainer>
                <CarouselModal
                    readOnly={readOnly}
                    allowExtraction={false}
                    isOpen={Boolean(previewImage)}
                    closeModal={event => {
                        if (event?.stopPropagation) {
                            event.stopPropagation();
                        }
                        setPreviewImage(null);
                    }}
                    items={previewImage ? [{ src: previewImage.src }] : []}
                    fileType="Image"
                />
            </>
        );
    }

    return (
        <>
            <NoteContainer
                role="button"
                tabIndex={0}
                onKeyDown={event => {
                    if (event.target !== event.currentTarget) return;
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        if (inline && !readOnly) onStartEditing();
                        else onOpenNote();
                    }
                }}
                onClick={event => {
                    if (event.target instanceof Element && event.target.closest('a, button, input, img, video')) {
                        return;
                    }

                    if (inline && !readOnly) onStartEditing();
                    else onOpenNote();
                }}
            >
                <ExpandablePreviewText lines={6} lineHeight={20} contentKey={displayText} expanded={noteExpanded} onExpandedChange={setNoteExpanded}>
                {displayText ? (
                    shouldRenderPlainText ? (
                        <TextPreview>{displayText}</TextPreview>
                    ) : (
                        <CardMarkdownPreview>
                            <Markdown options={compactCardMarkdownOptions}>{displayText}</Markdown>
                        </CardMarkdownPreview>
                    )
                ) : (
                    <TextPreview $isEmpty>{lnT('messages:addANote')}</TextPreview>
                )}
                </ExpandablePreviewText>
                {showHoverActions ? (
                    <CardHoverActions>
                        <CardHoverActionButton
                            type="button"
                            disabled={!values.body}
                            onMouseDown={event => {
                                event.preventDefault();
                            }}
                            onClick={event => {
                                event.preventDefault();
                                event.stopPropagation();
                                onCopyNote(normalizedNoteText);
                            }}
                        >
                            <FiCopy size={12} />
                        </CardHoverActionButton>
                        <CardHoverActionButton
                            type="button"
                            onMouseDown={event => {
                                event.preventDefault();
                            }}
                            onClick={event => {
                                event.preventDefault();
                                event.stopPropagation();
                                if (inline && !readOnly) onStartEditing();
                                else onOpenNote();
                            }}
                        >
                            {readOnly ? <FiMaximize2 size={12} /> : <FiEdit2 size={12} />}
                        </CardHoverActionButton>
                    </CardHoverActions>
                ) : null}
            </NoteContainer>
            <CarouselModal
                    readOnly={readOnly}
                    allowExtraction={false}
                isOpen={Boolean(previewImage)}
                closeModal={event => {
                    if (event?.stopPropagation) {
                        event.stopPropagation();
                    }
                    setPreviewImage(null);
                }}
                items={previewImage ? [{ src: previewImage.src }] : []}
                fileType="Image"
            />
        </>
    );
}

const noteMarkdownSurfaceCss = css`
    word-break: break-word;
    overflow-wrap: anywhere;

    > *:first-child {
        margin-top: 0 !important;
    }

    > *:last-child {
        margin-bottom: 0 !important;
    }

    p,
    li {
        white-space: pre-wrap;
    }

    ul,
    ol {
        margin: 4px 0;
        padding-left: 16px;
    }

    table {
        display: block;
        width: 100%;
        max-width: 100%;
        margin: 12px 0;
        overflow-x: auto;
        border-collapse: separate;
        border-spacing: 0;
        border: 1px solid rgba(255, 255, 255, 0.18);
        border-radius: 8px;
        background: rgba(255, 255, 255, 0.035);
    }

    th,
    td {
        min-width: 160px;
        width: 160px;
        max-width: 160px;
        border-right: 1px solid rgba(255, 255, 255, 0.18);
        border-bottom: 1px solid rgba(255, 255, 255, 0.18);
        padding: 10px 12px;
        vertical-align: top;
        text-align: left;
        font-size: 14px;
        line-height: 1.45;
    }

    th {
        color: rgba(255, 255, 255, 0.95);
        background: rgba(255, 255, 255, 0.08);
        font-weight: 600;
    }

    img,
    pre {
        max-width: 100%;
    }
`;

const FullPreviewContainer = styled.div`
    background-color: rgba(255, 255, 255, 0.03);
    border: 1px solid ${PALE_WHITE_1};
    border-radius: 12px;
    padding: 8px 10px;
    min-height: 300px;
    height: 100%;
    position: relative;
    display: flex;
    flex-direction: column;
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover {
        background-color: rgba(255, 255, 255, 0.06);
    }
`;

const FullPreviewContent = styled.div`
    min-height: 0;
    flex: 1;
    overflow: auto;
    color: rgba(255, 255, 255, 0.8);
    font-size: 14px;
    line-height: 1.4286;
    padding-right: 4px;
    ${noteMarkdownSurfaceCss}
`;

const NotePreviewPlaceholder = styled.div`
    color: rgba(255, 255, 255, 0.45);
    font-size: 14px;
`;

const PlainTextPreview = styled.div`
    white-space: pre-wrap;
    word-break: break-word;
`;

const TextPreview = styled.div`
    flex: 1;
    font-size: 14px;
    line-height: 1.4286;
    color: ${props => props.$isEmpty ? 'rgba(255,255,255,0.45)' : 'rgba(255,255,255,0.8)'};
    min-height: 24px;
    white-space: pre-wrap;
    word-break: break-word;
`;

const CardMarkdownPreview = styled.div`
    flex: 1;
    min-width: 0;
    min-height: 24px;
    word-break: break-word;
    overflow-wrap: anywhere;
    color: rgba(255, 255, 255, 0.8);
    font-size: 14px;
    line-height: 1.4286;
    ${noteMarkdownSurfaceCss}
`;

const CardHoverActions = styled.div`
    position: absolute;
    top: 6px;
    right: 0;
    z-index: 1;
    background: #222729;
    box-shadow: -6px 0 10px #222729;
    border-radius: 6px;
    display: flex;
    gap: 4px;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.15s ease;
`;

const CardHoverActionButton = styled.button`
    border: none;
    outline: none;
    width: 22px;
    height: 22px;
    border-radius: 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.08);
    color: rgba(255, 255, 255, 0.8);
    cursor: ${props => props.disabled ? 'default' : 'pointer'};
    opacity: ${props => props.disabled ? 0.35 : 1};
    pointer-events: ${props => props.disabled ? 'none' : 'auto'};

    &:hover {
        background: rgba(255, 255, 255, 0.14);
        color: ${WHITE};
    }
`;

const NoteContainer = styled.div`
    position: relative;
    padding: 8px 0 12px;
    margin-bottom: 8px;
    border-radius: 12px;
    display: flex;
    align-items: flex-start;
    cursor: pointer;
    transition: all 0.2s ease;

    &:focus-visible {
        outline: 1px solid rgba(255, 255, 255, 0.35);
        outline-offset: 3px;
    }
    &:hover ${CardHoverActions}, &:focus-within ${CardHoverActions} {
        opacity: 1;
        pointer-events: auto;
    }
`;

export default NoteSection;
